import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Lock } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { hasUserPermission } from '../../utils/permissionUtils';
import { hasRole, ROLES } from '../../utils/roleUtils';
import { cn } from '../../lib/utils';
import NavItem from './NavItem';
import ProfileMenu from './ProfileMenu';
import { NAV_ENTRIES, matchNavEntry } from './navigation';
import type { NavEntry } from './navigation';

export interface AppSidebarProps {
  username: string;
  email?: string;
  onLogout: () => void;
  /** Positioning is left to the caller: fixed column on desktop, sliding panel in the drawer. */
  className?: string;
  /** Called after a nav link is followed — the drawer closes on navigation. */
  onNavigate?: () => void;
}

const Eyebrow = ({ children }: { children: string }) => (
  <p className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
    {children}
  </p>
);

const AppSidebar = ({ username, email, onLogout, className, onNavigate }: AppSidebarProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { currentUser } = useContext(AppContext);
  // Not persisted: mounted outside `<Routes>`, this already survives every navigation (measured).
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  // `permissions` is an array as soon as `/api/me` has answered; until then the list would paint
  // empty and then fill in, so a skeleton stands in.
  const resolved = Array.isArray(currentUser?.permissions);
  const visible = resolved
    ? NAV_ENTRIES.filter(
        (entry) =>
          hasUserPermission(currentUser, entry.permission) &&
          (!entry.superAdminOnly || hasRole(currentUser?.role, ROLES.SUPERADMIN)),
      )
    : [];
  const activeEntry = matchNavEntry(pathname, visible);

  const childrenOf = (key: string) => visible.filter((entry) => entry.parent === key);
  // A child whose parent is hidden is promoted to top level rather than dropped with it.
  const isRoot = (entry: NavEntry) =>
    !entry.parent || !visible.some((candidate) => candidate.key === entry.parent);

  return (
    <aside
      className={cn(
        'flex w-[248px] flex-col border-r border-zinc-200 bg-white',
        'dark:border-zinc-800 dark:bg-zinc-950',
        className,
      )}
    >
      <Link
        to="/home"
        className={cn(
          'flex h-14 shrink-0 items-center gap-2.5 border-b border-zinc-200 px-4 dark:border-zinc-800',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900',
          'dark:focus-visible:ring-zinc-100',
        )}
      >
        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-zinc-900 text-[11px] font-bold tracking-tight text-white dark:bg-zinc-100 dark:text-zinc-900">
          GPS
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-50">GPS Admin</span>
          <span className="truncate text-[11px] text-zinc-500 dark:text-zinc-400">Globe Groupe</span>
        </span>
      </Link>

      <nav aria-label={t('Main navigation')} className="flex-1 overflow-y-auto px-3 pb-4">
        <Eyebrow>{t('Platform')}</Eyebrow>

        {!resolved && (
          <ul aria-label={t('Loading navigation')} aria-busy="true" className="space-y-1">
            {NAV_ENTRIES.map((entry) => (
              <li key={entry.key} className="h-9 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800" />
            ))}
          </ul>
        )}

        {resolved && visible.length === 0 && (
          <div className="mt-2 rounded-lg border border-dashed border-zinc-300 px-3 py-6 text-center dark:border-zinc-700">
            <Lock className="mx-auto h-[22px] w-[22px] text-zinc-400" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-zinc-900 dark:text-zinc-50">{t('No section available')}</p>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              {t('Ask an administrator to grant you access to a section.')}
            </p>
          </div>
        )}

        {resolved && visible.length > 0 && (
          <ul className="space-y-1">
            {visible.filter(isRoot).map((entry) => {
              const nested = childrenOf(entry.key);
              // A head label alone makes a group: Technicians has no second child in this lot.
              const isGroup = Boolean(entry.headLabel) || nested.length > 0;
              const inActiveGroup = nested.some((child) => child.key === activeEntry?.key);
              // Shut by default, except the group holding the route: else a child lands behind a shut parent.
              const expanded = toggled[entry.key] ?? (inActiveGroup || activeEntry?.key === entry.key);

              return (
                <li key={entry.key}>
                  <NavItem
                    to={entry.to}
                    label={entry.label(t)}
                    icon={entry.icon}
                    active={activeEntry?.key === entry.key}
                    inActiveGroup={inActiveGroup}
                    collapsible={isGroup}
                    expanded={expanded}
                    onToggle={() => setToggled((current) => ({ ...current, [entry.key]: !expanded }))}
                    onNavigate={onNavigate}
                  />
                  {isGroup && expanded && (
                    <ul className="mt-1 space-y-1">
                      {entry.headLabel && (
                        <li>
                          <NavItem
                            to={entry.to}
                            label={entry.headLabel(t)}
                            icon={ArrowRight}
                            active={pathname === entry.to}
                            indented
                            onNavigate={onNavigate}
                          />
                        </li>
                      )}
                      {nested.map((child) => (
                        <li key={child.key}>
                          <NavItem
                            to={child.to}
                            label={child.label(t)}
                            icon={child.icon}
                            active={activeEntry?.key === child.key}
                            indented
                            onNavigate={onNavigate}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      <div className="shrink-0 border-t border-zinc-200 px-3 pb-3 dark:border-zinc-800">
        <Eyebrow>{t('Account')}</Eyebrow>
        <ProfileMenu username={username} email={email} onLogout={onLogout} />
      </div>
    </aside>
  );
};

export default AppSidebar;
